import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { formatTime, MediaPlayer, MediaProvider, useMediaState } from '@vidstack/react'
import '@vidstack/react/player/styles/default/layouts/audio.css'
import '@vidstack/react/player/styles/default/theme.css'

import { useEffect, useMemo, useRef } from 'react'
import styled from 'styled-components'

import { breakpoints } from '@/config/layout'
import { mixpanel } from '@/mixpanel'

import type { MediaPlayerInstance } from '@vidstack/react'
import type { MouseEvent } from 'react'

type Props = {
  title: string
  audioUrl: string
  posterUrl?: string
  onComplete?: () => void
  start: boolean
}

export const AudioPlayer = ({ title, audioUrl, posterUrl, onComplete, start }: Props) => {
  const mediaPlayerRef = useRef<MediaPlayerInstance>(null)

  const isPaused = useMediaState('paused', mediaPlayerRef)
  const currentTime = useMediaState('currentTime', mediaPlayerRef)
  const duration = useMediaState('duration', mediaPlayerRef)
  const percentage = useMemo(() => (currentTime / duration) * 100, [currentTime, duration])

  useEffect(() => {
    if (start) {
      mixpanel.track('audio_play', { title })
      mediaPlayerRef.current?.play()
    }
  }, [start, title])

  const togglePlayPause = () => {
    if (isPaused) {
      mixpanel.track('audio_play', { title })
      mediaPlayerRef.current?.play()
    } else {
      mixpanel.track('audio_pause', { title })
      mediaPlayerRef.current?.pause()
    }
  }

  const handleProgressBarClick = (event: MouseEvent<HTMLDivElement>) => {
    const progressBar = event.target as HTMLDivElement

    if (progressBar) {
      const clickPositionX = event.clientX - progressBar.getBoundingClientRect().left
      const progressBarWidth = progressBar.clientWidth
      const seekTime = (clickPositionX / progressBarWidth) * duration

      if (mediaPlayerRef.current) {
        mediaPlayerRef.current.currentTime = seekTime
      }
    }
  }

  return (
    <AudioContainer>
      <StyledMediaPlayer
        ref={mediaPlayerRef}
        title={title}
        src={audioUrl}
        autoPlay={false}
        onEnded={() => {
          mixpanel.track('audio_complete', { title })
          onComplete?.()
        }}
      >
        <MediaProvider>
          <Hero bgUrl={posterUrl || ''} />
        </MediaProvider>

        {start ? (
          <>
            <ControlsContainer>
              <PlayPauseButton onClick={togglePlayPause}>
                <FontAwesomeIcon
                  icon={['fas', isPaused ? 'play' : 'pause']}
                  color='white'
                  className='w-6 h-6 stroke-white z-10'
                />
              </PlayPauseButton>

              <ProgressBarContainer>
                <TimeLabel>{formatTime(currentTime)}</TimeLabel>
                <ProgressBar onClick={handleProgressBarClick}>
                  <ProgressFiller style={{ width: `${percentage}%` }} />
                </ProgressBar>
                <TimeLabel>{formatTime(duration)}</TimeLabel>
              </ProgressBarContainer>
            </ControlsContainer>
          </>
        ) : null}
      </StyledMediaPlayer>
    </AudioContainer>
  )
}

const AudioContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: black; /* bg-base-black */
`

const StyledMediaPlayer = styled(MediaPlayer)`
  width: 100%;
  height: 100%;
  border: 0;
`
const Hero = styled.div<{ bgUrl: string }>`
  background-image: url(${(props) => encodeURI(props.bgUrl)});
  width: 100%;
  height: 100%;
  flex-shrink: 0;
  background-size: cover;
  background-position: center;
`

const ControlsContainer = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(0, 0, 0, 0.5);
  @media ${breakpoints.toM} {
    padding-right: 50px;
  }
`

const PlayPauseButton = styled.div`
  display: flex;
  align-items: center;
  padding: 16px;
  cursor: pointer;

  .w-6 {
    width: 24px; /* Tailwind w-6 equivalent */
  }

  .h-6 {
    height: 24px; /* Tailwind h-6 equivalent */
  }

  .stroke-white {
    stroke: white;
  }

  .z-10 {
    z-index: 10;
  }
`

const ProgressBarContainer = styled.div`
  display: flex;
  flex-grow: 1;
  align-items: center;
  gap: 12px;
  padding-right: 24px; /* Tailwind pr-6 equivalent */
`

const TimeLabel = styled.p`
  width: 50px;
  font-weight: 600; /* Tailwind font-semibold */
  color: white; /* Tailwind text-base-white equivalent */
`

const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background-color: #e0e0e0; /* Tailwind bg-primary-100 equivalent */
  border-radius: 4px;
  overflow: hidden;
  cursor: pointer;
`

const ProgressFiller = styled.div`
  height: 8px;
  background-color: #4a90e2; /* Tailwind bg-primary-500 equivalent */
  border-radius: 4px;
  transition: width 0.2s ease;
  pointer-events: none;
`
